import { Injectable } from '@angular/core';
import { PanelGeometry } from '../helpers/panelGeometry';

/**
 * Coordinates panel translucency during obstacle/walkway workflows.
 * Uses a reference-counted set of "reasons" to determine when panels should be translucent.
 * Panels become 40% transparent when at least one reason is active,
 * and return to 100% opacity when all reasons are cleared.
 */
@Injectable({ providedIn: 'root' })
export class PanelVisibilityCoordinatorService {
  private activeReasons = new Set<string>();

  constructor(private panelGeometry: PanelGeometry) {}

  /**
   * Enable a reason for panel translucency.
   * @param reason Unique identifier for the translucency reason
   */
  enableObstacleReason(reason: string): void {
    if (!reason) {
      return;
    }

    this.activeReasons.add(reason);

    // Always ensure panels are translucent when there are active reasons
    if (this.activeReasons.size > 0) {
      this.panelGeometry.setPanelsTranslucent(true);
    }
  }

  /**
   * Disable a reason for panel translucency.
   * @param reason Unique identifier for the translucency reason
   */
  disableObstacleReason(reason: string): void {
    if (!reason || !this.activeReasons.has(reason)) {
      return;
    }

    this.activeReasons.delete(reason);

    // Only restore opacity when ALL reasons are cleared
    if (this.activeReasons.size === 0) {
      this.panelGeometry.setPanelsTranslucent(false);
    }
  }

  /**
   * Clear all translucency reasons and restore panel opacity.
   */
  clearObstacleReasons(): void {
    if (this.activeReasons.size === 0) {
      return;
    }

    this.activeReasons.clear();
    this.panelGeometry.setPanelsTranslucent(false);
  }
}
